/**
 * Discord Webhook Notification Utility
 * Sends notifications to Discord channels for SOS, Complaints, and Withdrawals
 */

/**
 * Get Discord webhook URLs from settings
 * @param {object} settings - App settings object
 * @returns {object} Webhook URLs
 */
const getDiscordWebhooks = (settings) => {
  return {
    // Call Center Channel - for SOS and Complaints
    CALL_CENTER: settings?.discordCallCenter || process.env.REACT_APP_DISCORD_CALL_CENTER_WEBHOOK || '',
    // Finance Channel - for Withdrawal Requests
    FINANCE: settings?.discordFinance || process.env.REACT_APP_DISCORD_FINANCE_WEBHOOK || ''
  };
};

/**
 * Send a notification to Discord
 * @param {string} webhookUrl - The Discord webhook URL
 * @param {object} embed - The embed object to send
 * @returns {Promise} - Promise that resolves when message is sent
 */
const sendDiscordNotification = async (webhookUrl, embed) => {
  if (!webhookUrl) {
    console.warn('Discord webhook URL not configured');
    return;
  }

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        embeds: [embed],
        username: 'Donkey Drive Alert System',
        avatar_url: 'https://donkey-drive-5cdd1.web.app/static/media/logowebsite.3ba0648eff59e4bdf030.png' // You can replace with your logo
      })
    });

    if (!response.ok) {
      console.error('Failed to send Discord notification:', response.status, response.statusText);
    }
  } catch (error) {
    console.error('Error sending Discord notification:', error);
  }
};

/**
 * Send SOS notification to Discord
 * @param {object} sosData - The SOS data object
 * @param {object} settings - App settings for formatting and webhook URLs
 */
export const sendSOSNotification = async (sosData, settings = {}) => {
  const webhooks = getDiscordWebhooks(settings);
  const embed = {
    title: '🆘 NEW SOS ALERT',
    color: 0xFF0000, // Red color
    fields: [
      {
        name: '📋 Booking ID',
        value: sosData.bookingId || 'N/A',
        inline: true
      },
      {
        name: '👤 User Name',
        value: sosData.user_name || 'Unknown',
        inline: true
      },
      {
        name: '📱 Contact',
        value: sosData.contact || 'N/A',
        inline: true
      },
      {
        name: '🚗 User Type',
        value: sosData.user_type || 'N/A',
        inline: true
      },
      {
        name: '📍 Location',
        value: sosData.location ? `[View on Map](https://maps.google.com/?q=${sosData.location.lat},${sosData.location.lng})` : 'Not available',
        inline: true
      },
      {
        name: '🕐 Time',
        value: new Date(sosData.complainDate || Date.now()).toLocaleString(),
        inline: true
      }
    ],
    footer: {
      text: 'Donkey Drive SOS System',
      icon_url: 'https://donkey-drive-5cdd1.web.app/static/media/logowebsite.3ba0648eff59e4bdf030.png'
    },
    timestamp: new Date().toISOString(),
    thumbnail: {
      url: 'https://i.imgur.com/wSTFkRM.png' // SOS icon
    }
  };

  if (sosData.message) {
    embed.fields.push({
      name: '💬 Message',
      value: sosData.message,
      inline: false
    });
  }

  await sendDiscordNotification(webhooks.CALL_CENTER, embed);
};

/**
 * Send Complaint notification to Discord
 * @param {object} complaintData - The complaint data object
 * @param {object} settings - App settings for formatting and webhook URLs
 */
export const sendComplaintNotification = async (complaintData, settings = {}) => {
  const webhooks = getDiscordWebhooks(settings);
  const embed = {
    title: '📢 NEW COMPLAINT',
    color: 0xFFA500, // Orange color
    fields: [
      {
        name: '👤 Customer Name',
        value: `${complaintData.firstName || ''} ${complaintData.lastName || ''}`.trim() || 'Unknown',
        inline: true
      },
      {
        name: '📧 Email',
        value: complaintData.email || 'N/A',
        inline: true
      },
      {
        name: '📱 Mobile',
        value: complaintData.mobile || 'N/A',
        inline: true
      },
      {
        name: '🏷️ User Type',
        value: complaintData.role || 'N/A',
        inline: true
      },
      {
        name: '📝 Subject',
        value: complaintData.subject || 'No subject',
        inline: false
      },
      {
        name: '💬 Message',
        value: complaintData.body ? (complaintData.body.length > 1000 ? complaintData.body.substring(0, 1000) + '...' : complaintData.body) : 'No message',
        inline: false
      },
      {
        name: '🕐 Submitted',
        value: new Date(complaintData.complainDate || Date.now()).toLocaleString(),
        inline: true
      }
    ],
    footer: {
      text: 'Donkey Drive Complaint System',
      icon_url: 'https://donkey-drive-5cdd1.web.app/static/media/logowebsite.3ba0648eff59e4bdf030.png'
    },
    timestamp: new Date().toISOString(),
    thumbnail: {
      url: 'https://i.imgur.com/DFmPIkE.png' // Complaint icon
    }
  };

  await sendDiscordNotification(webhooks.CALL_CENTER, embed);
};

/**
 * Send Withdrawal Request notification to Discord
 * @param {object} withdrawData - The withdrawal data object
 * @param {object} settings - App settings for formatting and webhook URLs
 */
export const sendWithdrawalNotification = async (withdrawData, settings = {}) => {
  const webhooks = getDiscordWebhooks(settings);
  // Format amount with currency symbol
  let amountDisplay = withdrawData.amount || 0;
  if (settings.symbol) {
    amountDisplay = settings.swipe_symbol 
      ? `${withdrawData.amount} ${settings.symbol}`
      : `${settings.symbol} ${withdrawData.amount}`;
  }

  const embed = {
    title: '💰 NEW WITHDRAWAL REQUEST',
    color: 0x00FF00, // Green color
    fields: [
      {
        name: '🆔 Request ID',
        value: withdrawData.id || 'N/A',
        inline: true
      },
      {
        name: '👤 Driver Name',
        value: withdrawData.name || 'Unknown',
        inline: true
      },
      {
        name: '💵 Amount',
        value: amountDisplay,
        inline: true
      },
      {
        name: '🏦 Bank Name',
        value: withdrawData.bankName || 'N/A',
        inline: true
      },
      {
        name: '🔢 Bank Account',
        value: withdrawData.bankAccount || 'N/A',
        inline: true
      },
      {
        name: '📱 Contact',
        value: withdrawData.mobile || 'N/A',
        inline: true
      },
      {
        name: '🕐 Request Date',
        value: new Date(withdrawData.date || Date.now()).toLocaleString(),
        inline: false
      }
    ],
    footer: {
      text: 'Donkey Drive Finance System',
      icon_url: 'https://donkey-drive-5cdd1.web.app/static/media/logowebsite.3ba0648eff59e4bdf030.png'
    },
    timestamp: new Date().toISOString(),
    thumbnail: {
      url: 'https://i.imgur.com/3elNhQu.png' // Money icon
    }
  };

  if (withdrawData.bankCode) {
    embed.fields.splice(4, 0, {
      name: '🏦 Bank Code',
      value: withdrawData.bankCode,
      inline: true
    });
  }

  await sendDiscordNotification(webhooks.FINANCE, embed);
};

/**
 * Send a custom notification to Discord
 * @param {string} channel - 'CALL_CENTER' or 'FINANCE'
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {number} color - Embed color (optional)
 * @param {object} settings - App settings for webhook URLs (optional)
 */
export const sendCustomNotification = async (channel, title, message, color = 0x0099FF, settings = {}) => {
  const webhooks = getDiscordWebhooks(settings);
  const webhookUrl = webhooks[channel];
  
  if (!webhookUrl) {
    console.warn(`Discord webhook URL not configured for channel: ${channel}`);
    return;
  }

  const embed = {
    title: title,
    description: message,
    color: color,
    footer: {
      text: 'Donkey Drive System',
      icon_url: 'https://donkey-drive-5cdd1.web.app/static/media/logowebsite.3ba0648eff59e4bdf030.png'
    },
    timestamp: new Date().toISOString()
  };

  await sendDiscordNotification(webhookUrl, embed);
};

/**
 * Test Discord webhook connections
 * @param {object} settings - App settings for webhook URLs (optional)
 */
export const testDiscordWebhooks = async (settings = {}) => {
  console.log('Testing Discord webhooks...');
  const webhooks = getDiscordWebhooks(settings);
  
  if (webhooks.CALL_CENTER) {
    await sendCustomNotification('CALL_CENTER', '✅ Test Message', 'Call Center webhook is working!', 0x00FF00, settings);
    console.log('Call Center webhook test sent');
  } else {
    console.warn('Call Center webhook URL not configured');
  }

  if (webhooks.FINANCE) {
    await sendCustomNotification('FINANCE', '✅ Test Message', 'Finance webhook is working!', 0x00FF00, settings);
    console.log('Finance webhook test sent');
  } else {
    console.warn('Finance webhook URL not configured');
  }
};

// Export webhook URLs for configuration checking
export const getWebhookStatus = (settings = {}) => {
  const webhooks = getDiscordWebhooks(settings);
  return {
    callCenter: !!webhooks.CALL_CENTER,
    finance: !!webhooks.FINANCE
  };
};
